import axios from 'axios'
import dayjs from "dayjs";
import _ from "lodash";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { API } from "../../../helpers/constants";
import { handleAxiosErrors, logoutUnauthorizedUser } from "../../../helpers/errorHandling";
import useUserAction from "../../../hooks/user/useUserAction";
import useUserData from "../../../hooks/user/useUserData";
import { ISSUES_FORM } from '../locales/namespaces'

const useIssue = ({ IssueId }) => {
	const { t } = useTranslation(ISSUES_FORM)
	const [loading, setLoading] = useState(true)
	const { token, username, userId, isLoggedIn, dateFormat, timeFormat } = useUserData()
	const { userLogout } = useUserAction()
	const [issue, setIssue] = useState({})
	const [staticData, setStaticData] = useState({
		addresses: [],
		categories: [], //itemCategories
		contacts: [],
		customers: [],
		items: [],
		kinds: [],
		priorities: [],
		projects: [],
		serviceItems: [],
		sites: [],
		slas: [],
		assets: [],
		statuses: [],
		technicians: [],
		types: [],
		types2: [],
		userTeam: [],
		users: [],
		suppliers: null,
		teams:[],
		teamUsers: [],
	})
	
	const issueDataManager = useCallback(() => {
		if (!IssueId) {
			setLoading(true)
			axios({
				method: 'post',
				url: `${API}/en/issues/create`,
				headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
			}).then((res) => {
				const data = res.data
				const issue = data.issue ? data.issue : {}
				setStaticData(prevState => ({
					...prevState,
					...data,
					kinds: data.kinds && data.kinds?.length > 0 ? _.orderBy(data.kinds, ['Description'], ['asc']) : [],
					priorities: data.priorities && data.priorities?.length > 0 ? _.orderBy(data.priorities, ['Description'], ['asc']) : [],
					statuses: data.statuses && data.statuses?.length > 0 ? _.orderBy(data.statuses, ['Description'], ['asc']) : [],
					technicians: data.technicians && data.technicians?.length > 0 ? data.technicians.map(tech => ({
						...tech,
						Description: tech.Description || tech.TechName || tech.Name || ''
					})) : [],
					types: data.types && data.types?.length > 0 ? _.orderBy(data.types, ['Description'], ['asc']) : [],
					types2: data.types2 && data.types2?.length > 0 ? _.orderBy(data.types2, ['Description'], ['asc']) : [],
					users: [],
					addresses: data.addresses && data.addresses?.length > 0 ? data.addresses.map(addr => ({
						...addr,
						id: addr.id,
						Description: addr.MainAddress
					})) : [],
					sites: data.sites && data.sites?.length > 0 ? data.sites.map(site => ({
						...site,
						Description: site.Site
					})) : [],
					contacts: data.constacts && data.constacts?.length > 0 ? _.orderBy(data.contacts.map(contact => ({
						...contact,
						id: contact.ContactId,
						Description: contact.ContactName
					})), ['Description'], ['asc']) : [],
				}))
				setIssue({
					...issue,
					CreatorId: data.creatorId ? res.data.creatorId : userId,
					Creator: data.creator ? res.data.creator : username,
					CustomerId: issue.CustomerId && issue.CustName ? {
						id: issue.CustomerId,
						Description: issue.CustName,
						MainContactName: issue.MainContactName,
						MainContactPhone: issue.MainContactPhone,
						MainContactEmail: issue.MainContactEmail
					} : '',
					ContactId: issue.ContactId && issue.ContactName ? {
						id: issue.ContactId,
						Description: issue.ContactName,
						ContactPhone: issue.ContactPhone,
						ContactEmail: issue.ContactEmail
					} : '',
					AddressId: issue?.AddressId && issue.MainAddress ? {
						id: issue?.AddressId,
						Description: issue.MainAddress,
						FmEmail: issue.FmEmail,
						FmName: issue.FmName,
					} : '',
					PriorityId: res.data.priorities.length > 0 && res.data.priorities.find(f => f.isDefault === '1') !== undefined ? res.data.priorities.find(f => f.isDefault === '1').id : '',
					TypeId: res.data.types.length > 0 && res.data.types.find(f => f.isDefault === '1') !== undefined ? res.data.types.find(f => f.isDefault === '1').id : '',
					StatusId: res.data.statuses.length > 0 && res.data.statuses.find(f => f.isDefault === '1') !== undefined ? res.data.statuses.find(f => f.isDefault === '1').id : '',
					KindId: res.data.kinds.length > 0 && res.data.kinds.find(f => f.isDefault === '1') !== undefined ? res.data.kinds.find(f => f.isDefault === '1').id : '',
					Type2Id: res.data.types2 && res.data.types2?.length > 0 && res.data.types2.find(f => f.isDefault === '1') !== undefined ? res.data.types2.find(f => f.isDefault === '1') : '',
					TeamId: res.data.userTeam.length > 0 && res.data.userTeam.find(f => f.isDefault === '1') !== undefined ? res.data.userTeam.find(f => f.isDefault === '1').id : '',
					EstimStartDt: issue.EstimStartDt ? issue.EstimStartDt : '',
					EstimDueDt: issue.EstimDueDt ? issue.EstimDueDt : '',
					EstimStartTm: issue.EstimStartTm ? issue.EstimStartTm : '',
					EstimDueTm: issue.EstimDueTm ? issue.EstimDueTm : '',
					TargetedStartDt: issue.TargetedStartDt ? issue.TargetedStartDt : '',
					TargetedEndDt: issue.TargetedEndDt ? issue.TargetedEndDt : '',
					DateReg: issue.DateReg ? issue.DateReg : '',
					TargetedStartTm: issue.TargetedStartTm ? issue.TargetedStartTm : '',
					TargetedEndTm: issue.TargetedEndTm ? issue.TargetedEndTm : '',
					ActualVisitDt: issue.ActualVisitDt ? issue.ActualVisitDt : '',
					ActualVisitTm: issue.ActualVisitTm ? issue.ActualVisitTm : '',
				})
				setLoading(false)
			}).catch(error => {
				enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
					variant: 'error',
					autoHideDuration: 3000,
				})
				setLoading(false)
				logoutUnauthorizedUser(error, isLoggedIn, userLogout)
			})
		} else {
			setLoading(true)
			axios({
				method: 'post',
				url: API + '/en/issues/edit/' + IssueId,
				headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
			}).then(res => {
				let data = res.data
				if (data && data.issue.length > 0) {
					const issue = data.issue[0]
					setIssue({
						...issue,
						isActive: issue.isActive === '1',
						isOnBehalf: issue.isOnBehalf === '1',
						isBillable: issue.isBillable === '1',
						isNotSla: issue.isNotSla === '1',
						ActualStartDt: issue.ActualStartDt ? issue.ActualStartDt : '',
						ActualEndDt: issue.ActualEndDt ? issue.ActualEndDt : '',
						SubmitDt: issue.SubmitDt ? issue.SubmitDt : '',
						EstimStartDt: issue.EstimStartDt ? issue.EstimStartDt : '',
						EstimDueDt: issue.EstimDueDt ? issue.EstimDueDt : '',
						EstimStartTm: issue.EstimStartTm ? issue.EstimStartTm : '',
						EstimDueTm: issue.EstimDueTm ? issue.EstimDueTm : '',
						TargetedStartDt: issue.TargetedStartDt ? issue.TargetedStartDt : '',
						TargetedEndDt: issue.TargetedEndDt ? issue.TargetedEndDt : '',
						DateReg: issue.DateReg ? issue.DateReg : '',
						TargetedStartTm: issue.TargetedStartTm ? issue.TargetedStartTm : '',
						TargetedEndTm: issue.TargetedEndTm ? issue.TargetedEndTm : '',
						ActualVisitDt: issue.ActualVisitDt ? issue.ActualVisitDt : '',
						ActualVisitTm: issue.ActualVisitTm ? issue.ActualVisitTm : '',
						InternalAssignmentId: issue.InternalAssignmentId ? issue.InternalAssignmentId : '',
						InternalAssignmentTeamId: issue.InternalAssignmentTeamId ? issue.InternalAssignmentTeamId : '',
						AssigneeId: issue.AssigneeId && issue.Assignee ? {
							id: issue.AssigneeId,
							Description: issue.Assignee
						} : '',
						Type2: issue.Type2Id && issue.Type2 ? {
							id: issue.Type2Id,
							Description: issue.Type2
						} : '',
						AddressId: issue?.AddressId && issue.MainAddress ? {
							id: issue?.AddressId,
							Description: issue.MainAddress,
							FmEmail: issue.FmEmail,
							FmName: issue.FmName,
						} : '',
						CustomerId: issue.CustomerId && issue.CustName ? {
							id: issue.CustomerId,
							Description: issue.CustName,
							MainContactName: issue.MainContactName,
							MainContactPhone: issue.MainContactPhone,
							MainContactEmail: issue.MainContactEmail,
						} : '',
						ContactId: issue.ContactId && issue.ContactName ? {
							id: issue.ContactId,
							Description: issue.ContactName,
							ContactPhone: issue.ContactPhone,
							ContactEmail: issue.ContactEmail
						} : '',
						files: issue.Attachments ? issue.Attachments.map(file => ({
							id: file.id,
							name: file.OriginFileName,
							user: file.LoginName,
							uploadedAt: dayjs(file.LastModifiedAt).format(`${dateFormat} ${timeFormat}`),
							disableRemove: true,
							url: `${API}/en/attachments/issue/${file.id}`,
						})) : [],
					})
					setStaticData(prevData => ({
						...prevData,
						...data,
						kinds: data.kinds && data.kinds?.length > 0 ? _.orderBy(data.kinds, ['Description'], ['asc']) : [],
						priorities: data.priorities && data.priorities?.length > 0 ? _.orderBy(data.priorities, ['Description'], ['asc']) : [],
						statuses: data.statuses && data.statuses?.length > 0 ? _.orderBy(data.statuses, ['Description'], ['asc']) : [],
						technicians: data.technicians && data.technicians?.length > 0 ? data.technicians.map(tech => ({
							...tech,
							Description: tech.Description || tech.TechName || tech.Name || ''
						})) : [],
						types: data.types && data.types?.length > 0 ? _.orderBy(data.types, ['Description'], ['asc']) : [],
						types2: data.types2 && data.types2?.length > 0 ? _.orderBy(data.types2, ['Description'], ['asc']) : [],
						addresses: data.addresses && data.addresses?.length > 0 ? data.addresses.map(addr => ({
							...addr,
							id: addr.id,
							Description: addr.MainAddress
						})) : [],
						sites: data.sites && data.sites?.length > 0 ? data.sites.map(site => ({
							...site,
							Description: site.Site
						})) : [],
						contacts: data.constacts && data.constacts?.length > 0 ? _.orderBy(data.contacts.map(contact => ({
							...contact,
							id: contact.ContactId,
							Description: contact.ContactName
						})), ['Description'], ['asc']) : [],
					}));
				}
				setLoading(false)
			}).catch((error) => {
				enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
					variant: 'error',
					autoHideDuration: 3000,
				})
				setLoading(false)
				logoutUnauthorizedUser(error, isLoggedIn, userLogout)
			})
		}
	}, [IssueId, userId, username, t, isLoggedIn, userLogout, dateFormat])
	
	useEffect(() => {
		issueDataManager()
	}, [IssueId])
	return {
		loading,
		issue,
		staticData,
		issueDataManager
	}
	
}
export default useIssue

